import React, { useState, useRef, useEffect } from "react";
import Modal from "../../Components/Base/Modal";

export default function ModalConfirmSubscriptionAction(props) {
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef(null);

  function lowerCaseFirstLetter(str) {
    if (!str) {
      return "";
    }
    return str.charAt(0).toLowerCase() + str.slice(1);
  }

  const handleConfirm = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      await props.onConfirm();
    } catch (error) {
      console.error("Confirmation error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        if (!isLoading) {
          props.close();
        }
      }
    }

    // Handle Escape key press
    function handleKeyDown(event) {
      if (event.key === 'Escape' && !isLoading) {
        props.close();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isLoading, props]);

  return (
    <Modal>
      {/* Overlay */}
      <div className="fixed inset-0 bg-opacity-50 flex items-center justify-center z-50">
        {/* Modal container with ref for outside click detection */}
        <div 
          ref={modalRef}
          className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden mx-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          {/* Header */}
          <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50">
            <div className="flex items-center justify-center w-16 h-16 bg-white rounded-full mx-auto mb-4 shadow-md">
              <svg
                className="w-8 h-8 text-emerald-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={props.action === "Upgrade" || props.action === "Downgrade" 
                    ? "M5 13l4 4L19 7" 
                    : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"}
                />
              </svg>
            </div>
            <h3 id="modal-title" className="text-2xl font-bold text-emerald-900 text-center">
              {props.action === "Upgrade" || props.action === "Downgrade" 
                ? `${props.action} Plan?` 
                : "Subscribe to Plan?"}
            </h3>
          </div>

          {/* Body */}
          <div className="p-6">
            <p id="modal-description" className="text-gray-600 text-center">
              Are you sure you want to{" "}
              <span className="font-medium text-emerald-600">
                {props.action === "Upgrade" || props.action === "Downgrade"
                  ? lowerCaseFirstLetter(props.action)
                  : "subscribe for selected"}
              </span>{" "}
              plan?
            </p>
          </div>

          {/* Footer - Buttons */}
          <div className="px-6 py-4 bg-gray-50 flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-3">
            <button
              className="px-10 py-3 text-base font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={props.close}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className={`px-10 py-3 text-base font-semibold text-white bg-gradient-to-r from-emerald-500 to-green-600 rounded-lg hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all duration-200 flex items-center justify-center ${
                isLoading ? "opacity-75 cursor-not-allowed" : "hover:from-emerald-600 hover:to-green-700"
              }`}
              onClick={handleConfirm}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : (
                "Confirm"
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}